/**
 * The chrome Mod ships, registered from one place (RFC 0284 S3/S6).
 *
 * WHAT THIS REPLACES. Each built-in used to call `registerBuiltin` at the
 * bottom of its own module, and `WorldShell` reached them with three bare
 * side-effect imports:
 *
 *   import './chrome/agentRenderer'
 *   import './chrome/headerSlot'
 *   import './chrome/sidebarSlot'
 *
 * That works and it is a trap. An import with no binding reads as dead to every
 * reader and every tool that tidies unused imports, and deleting one does not
 * break the build: it deletes the sidebar, at runtime, silently. Load-bearing
 * code should not look like something you can clean up.
 *
 * It also made the registry's own tests fragile enough to need a warning. Both
 * suites carry the note "FIRST, before any reset: importing the module is what
 * registers the built-in", because the assertion depended on import order and a
 * reset in an earlier test would have made it pass for the wrong reason. A test
 * that has to run first is a test about module loading, not about the registry.
 *
 * SO REGISTRATION BECOMES A CALL. One function, imported as a value, called
 * where the shell starts. The dependency is visible, the order is written down
 * rather than inferred, and a test that wants the shipped chrome asks for it.
 *
 * STILL A REGISTRY, deliberately. The alternative is a static list that
 * `builtinFillsFor` reads, which removes the mutable global entirely. It also
 * inverts the dependency (`slots.ts` would import the fills that import
 * it) and would have to thread a list through every resolver. The hazard here
 * was the invisible import, not the map, so this fixes that and leaves the
 * larger change to be made on purpose or not at all.
 */

import { builtinAgentRenderer } from './agent/agentRenderer'
import { builtinComposer } from './composer/composerSlot'
import { builtinHeader } from './header/headerSlot'
import { registerRenderer } from './renderers'
import {
  builtinSessionChanges,
  builtinSessionTasks,
} from './session-header/sessionHeaderSlot'
import { builtinSidebar } from './sidebar/sidebarSlot'
import { registerBuiltin } from './slots'

/**
 * Put the shipped chrome in the registries.
 *
 * IDEMPOTENT, because both registries replace by id rather than appending. Safe
 * to call from a hot reload, from a second entry point, and from a test that has
 * just reset the registry to prove something about an empty one.
 */
export function registerBuiltins(): void {
  registerBuiltin(builtinSidebar)
  registerBuiltin(builtinHeader)
  registerBuiltin(builtinComposer)
  registerBuiltin(builtinSessionChanges)
  registerBuiltin(builtinSessionTasks)
  registerRenderer(builtinAgentRenderer)
}
